/**
 * Agent mind — soul, versions, memory, hero, decided (spec #42 §5).
 *
 * Read-only in this step: editing the soul or the leash is step 7's, and
 * approving an agent's own soul proposal goes through the EXISTING
 * `/api/action-proposals/{id}/approve` — there is no second write path.
 */

import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';
import type {
  DecidedResponse,
  MemoryHeroResponse,
  MemoryResponse,
  SoulResponse,
  SoulVersionsResponse,
} from '../types/agentMind';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

function agentPath(domain: string, suffix: string): string {
  return `${API_BASE_URL}/api/agents/${encodeURIComponent(domain)}${suffix}`;
}

export async function getSoul(domain: string): Promise<SoulResponse> {
  const response = await fetch(agentPath(domain, '/soul'), {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<SoulResponse>(response);
}

export async function getSoulVersions(
  domain: string,
  limit = 20
): Promise<SoulVersionsResponse> {
  const response = await fetch(
    agentPath(domain, `/soul/versions?limit=${limit}`),
    { method: 'GET', headers: createHeaders() }
  );
  return handleResponse<SoulVersionsResponse>(response);
}

/** `kinds=` filters `items` only — never `counts_by_kind`, never the hero
 * (#42 §5). The caller cannot change that here, and should not want to. */
export async function getMemory(
  domain: string,
  options: { limit?: number; offset?: number; kinds?: string[] } = {}
): Promise<MemoryResponse> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.offset !== undefined)
    params.set('offset', String(options.offset));
  if (options.kinds?.length) params.set('kinds', options.kinds.join(','));
  const qs = params.toString();
  const response = await fetch(agentPath(domain, `/memory${qs ? `?${qs}` : ''}`), {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<MemoryResponse>(response);
}

/** One entry, or `{entry: null, reason}` — the hero's own empty state. */
export async function getMemoryHero(
  domain: string
): Promise<MemoryHeroResponse> {
  const response = await fetch(agentPath(domain, '/memory/hero'), {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<MemoryHeroResponse>(response);
}

export async function getDecided(
  domain: string,
  limit = 20
): Promise<DecidedResponse> {
  const response = await fetch(agentPath(domain, `/decided?limit=${limit}`), {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<DecidedResponse>(response);
}
